/**
 * Missed Call Toast — persistent notification for missed inbound calls.
 *
 * - Stays visible until user dismisses or calls back the same number.
 * - Groups multiple missed calls from the same number into a single toast
 *   showing a count badge instead of duplicate toasts.
 */

import { useMemo, useCallback } from 'react';
import { Phone, PhoneMissed, X } from 'lucide-react';
import { useCallStore } from '@/stores/call-store';

interface GroupedMissedCall {
  /** The customer phone number (group key) */
  number: string;
  /** How many missed calls from this number */
  count: number;
  /** IDs of all missed calls in this group (for dismissal) */
  ids: string[];
}

export function MissedCallToast() {
  const missedCalls = useCallStore((s) => s.missedCalls);
  const activeCalls = useCallStore((s) => s.activeCalls);
  const dismissMissedCall = useCallStore((s) => s.dismissMissedCall);
  const setDialerNumber = useCallStore((s) => s.setDialerNumber);
  const toggleCallManager = useCallStore((s) => s.toggleCallManager);
  const callManagerOpen = useCallStore((s) => s.callManagerOpen);

  // Check if there's an incoming call toast showing (to offset position)
  const hasIncomingRinging = activeCalls.some(
    (c) => c.direction === 'inbound' && c.status === 'ringing'
  );

  // Group missed calls by phone number
  const grouped = useMemo<GroupedMissedCall[]>(() => {
    const map = new Map<string, GroupedMissedCall>();
    for (const call of missedCalls) {
      const num = call.customerNumber || 'Unknown';
      const existing = map.get(num);
      if (existing) {
        existing.count += 1;
        existing.ids.push(call.id);
      } else {
        map.set(num, { number: num, count: 1, ids: [call.id] });
      }
    }
    return Array.from(map.values());
  }, [missedCalls]);

  const handleDismissGroup = useCallback(
    (ids: string[]) => {
      ids.forEach((id) => dismissMissedCall(id));
    },
    [dismissMissedCall]
  );

  const handleCallBack = useCallback(
    (number: string, ids: string[]) => {
      setDialerNumber(number);
      if (!callManagerOpen) {
        toggleCallManager();
      }
      // Dismiss all missed calls from this number
      ids.forEach((id) => dismissMissedCall(id));
    },
    [setDialerNumber, toggleCallManager, callManagerOpen, dismissMissedCall]
  );

  if (grouped.length === 0) return null;

  return (
    <div
      className="fixed right-4 z-[59] flex flex-col gap-2 w-80"
      style={{ top: hasIncomingRinging ? '8rem' : '1rem' }}
    >
      {grouped.map((group) => (
        <div
          key={group.number}
          className="animate-in slide-in-from-top-2 fade-in duration-300 rounded-xl border border-amber-500/30 bg-background/95 backdrop-blur-sm shadow-2xl overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center gap-3 px-4 pt-3 pb-2">
            <div className="relative flex h-9 w-9 items-center justify-center rounded-full bg-amber-500/15 shrink-0">
              <PhoneMissed className="h-4 w-4 text-amber-500" />
              {group.count > 1 && (
                <span className="absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-amber-500 px-1 text-[10px] font-bold text-white">
                  {group.count}
                </span>
              )}
            </div>

            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-amber-600 dark:text-amber-400">
                Missed Call{group.count > 1 ? `s (${group.count})` : ''}
              </p>
              <p className="text-sm font-semibold text-foreground truncate">
                {group.number}
              </p>
            </div>

            <button
              onClick={() => handleDismissGroup(group.ids)}
              className="shrink-0 rounded-full p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              title="Dismiss"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-2 px-4 pb-3">
            <button
              onClick={() => handleDismissGroup(group.ids)}
              className="flex-1 flex items-center justify-center gap-1.5 rounded-lg bg-muted/50 px-3 py-2 text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            >
              Dismiss
            </button>
            {group.number !== 'Unknown' && (
              <button
                onClick={() => handleCallBack(group.number, group.ids)}
                className="flex-1 flex items-center justify-center gap-1.5 rounded-lg bg-emerald-500 px-3 py-2 text-xs font-medium text-white hover:bg-emerald-600 transition-colors"
              >
                <Phone className="h-3.5 w-3.5" />
                Call Back
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
